/**
 * Search Inputs Tools for FlowRide Frontend
 * Provides utility functions for filtering and managing search history.
 * Handles deduplication and sorting of search suggestions.
 */

import { GeocodeSource, GeocodeType, SearchResultItem } from "../types/geocode-ui.types";
import { SearchHistoryItem } from "../types/GlobalContext";

const EARTH_RADIUS_METERS = 6371000;
const POSITION_DEDUP_TOLERANCE_METERS = 5;

const toRadians = ( degrees: number ): number => {
    return ( degrees * Math.PI ) / 180;
};

const distanceMeters = ( lat1: number, lon1: number, lat2: number, lon2: number ): number => {
    const dLat = toRadians( lat2 - lat1 );
    const dLon = toRadians( lon2 - lon1 );
    const lat1Rad = toRadians( lat1 );
    const lat2Rad = toRadians( lat2 );

    const a = Math.sin( dLat / 2 ) ** 2 +
      Math.cos( lat1Rad ) * Math.cos( lat2Rad ) * Math.sin( dLon / 2 ) ** 2;
    const c = 2 * Math.atan2( Math.sqrt( a ), Math.sqrt( 1 - a ) );

    return EARTH_RADIUS_METERS * c;
};

/**
 * Filter search history based on query and usage patterns
 * @param query - Search query string
 * @param isSource - Whether filtering for source (departure) locations
 * @param searchHistory - Array of search history items
 * @returns Filtered and sorted search history with source property
 */
export const filterSearchHistory = ( query: string, searchHistory: SearchHistoryItem[] ) => {
    // Filter out invalid items (missing required properties) and filter by query
    let filteredHistory = searchHistory.filter( ( item ) => {
        // Validate item has required properties
        if ( item == null || typeof item.result !== "string" || item.result.trim() === "" ) {
            return false;
        }

        return item.result.toLowerCase().includes( query.toLowerCase() );
    } );

    // Sort the filtered history by date (most recent first)
    filteredHistory = filteredHistory.sort( ( a, b ) => b.lastUsedDate - a.lastUsedDate );

    // Sort by sourceCount or destinationCount
    filteredHistory = filteredHistory.sort( ( a, b ) => {
        return b.usageCount - a.usageCount;
    } );

    filteredHistory = deduplicateHistory( filteredHistory );

    // Return the top 3 elements with added source property
    return filteredHistory.slice( 0, 3 ).map( ( item ) => ( {
        ...item,
        "source": GeocodeSource.LOCAL,
    } ) );
};

/**
 * Remove duplicate entries: by id, by near-identical coordinates, and for addresses by
 * street label plus location (coordinates or subtitle + id) so homonymous streets in different
 * cities are not collapsed.
 *
 * @param searchHistory - Array of search history or geocoding items
 * @returns Deduplicated search history array
 */
export const deduplicateHistory = <T extends SearchResultItem>( searchHistory: T[] ): T[] => {
    const seenIds = new Set<string>();
    const seenAddressKeys = new Set<string>();
    const seenCoordinates: Array<{
        "lat": number;
        "lon": number;
    }> = [];

    searchHistory = searchHistory.filter( ( item ) => {
        // Validate item has required properties
        if ( item == null || typeof item.id !== "string" || typeof item.result !== "string" ) {
            return false;
        }

        const id = item.id.trim().toLowerCase();
        const normalizedName = item.result.trim().toLowerCase();
        const lat = Number( item.location.coordinates.lat );
        const lon = Number( item.location.coordinates.lon );
        const isAddress = item.type === GeocodeType.ADDRESS;
        const hasValidCoordinates = Number.isFinite( lat ) && Number.isFinite( lon );

        if ( isAddress && normalizedName !== "" ) {
            const subtitleNorm = ( item.subtitle ?? "" ).trim().toLowerCase();
            const addressKey = hasValidCoordinates
                ? `${ normalizedName }\u001f${ lat.toFixed( 5 ) }\u001f${ lon.toFixed( 5 ) }`
                : `${ normalizedName }\u001f${ subtitleNorm }\u001f${ id }`;

            if ( seenAddressKeys.has( addressKey ) ) {
                return false;
            }

            seenAddressKeys.add( addressKey );
        }

        const hasDuplicateId = id !== "" && seenIds.has( id );
        const hasDuplicateCoordinates = hasValidCoordinates && seenCoordinates.some( ( coordinate ) => {
            return distanceMeters( coordinate.lat, coordinate.lon, lat, lon ) <= POSITION_DEDUP_TOLERANCE_METERS;
        } );

        if ( hasDuplicateId || hasDuplicateCoordinates ) {
            return false;
        }

        seenIds.add( id );
        if ( hasValidCoordinates ) {
            seenCoordinates.push( {
                lat,
                lon,
            } );
        }

        return true;
    } );


    return searchHistory;
};
